import React, { useState, useEffect } from 'react';
import { Plus, RefreshCw, Trash2, Search, Wallet, CheckCircle, XCircle, TrendingUp, Users, Calendar } from 'lucide-react';
import http from '../api/http';
import { useFiliale } from '../context/FilialeContext';
import BlueHeader from '../components/BlueHeader';
import * as XLSX from 'xlsx';

const CaisseSociale = () => {
  const { filialeId, isConsolidated, currentFiliale } = useFiliale();
  const [caisses, setCaisses] = useState([]);
  const [personnel, setPersonnel] = useState([]);
  const [stats, setStats] = useState({ total: 0, approuves: 0, non_approuves: 0, today: 0 });
  const [evolution, setEvolution] = useState([]);
  const [montantPaie, setMontantPaie] = useState({ montant: 0, paie_id: null });
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedCaisse, setSelectedCaisse] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [personnelSearch, setPersonnelSearch] = useState('');
  const [selectedPersonnel, setSelectedPersonnel] = useState(null);
  const [showPersonnelDropdown, setShowPersonnelDropdown] = useState(false);
  const [formData, setFormData] = useState({
    personnel_id: '',
    approbation: true
  });

  const loadData = async () => {
    try {
      setLoading(true);
      
      // Récupérer les caisses sociales
      let endpoint = '/api/caisse-sociale';
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        endpoint += `?filiale_id=${filialeId}`;
      }
      const caissesRes = await http.get(endpoint);
      setCaisses(caissesRes.data || []);

      // Récupérer les statistiques
      let statsEndpoint = '/api/caisse-sociale/stats';
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        statsEndpoint += `?filiale_id=${filialeId}`;
      }
      const statsRes = await http.get(statsEndpoint);
      setStats(statsRes.data || { total: 0, approuves: 0, non_approuves: 0, today: 0 });

      // Récupérer l'évolution
      let evolutionEndpoint = '/api/caisse-sociale/evolution';
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        evolutionEndpoint += `?filiale_id=${filialeId}`;
      }
      const evolutionRes = await http.get(evolutionEndpoint);
      setEvolution(evolutionRes.data || []);

      // Récupérer le montant de la paie en cours
      let montantEndpoint = '/api/caisse-sociale/montant-paie';
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        montantEndpoint += `?filiale_id=${filialeId}`;
      }
      const montantRes = await http.get(montantEndpoint);
      setMontantPaie(montantRes.data || { montant: 0, paie_id: null });

      // Récupérer le personnel actif
      const personnelRes = await http.get('/api/personnel');
      const allPersonnel = Array.isArray(personnelRes.data?.data) ? personnelRes.data.data : [];
      const activePersonnel = allPersonnel.filter(p => p.active === 1);
      
      // Filtrer par filiale si nécessaire
      if (!isConsolidated && filialeId && filialeId !== 'all') {
        const personnelInFiliale = activePersonnel.filter(p => p.filiale_id === parseInt(filialeId));
        setPersonnel(personnelInFiliale);
      } else {
        setPersonnel(activePersonnel);
      }
    } catch (error) {
      console.error('Erreur chargement données:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [filialeId, isConsolidated]);

  const handleCreate = () => {
    setFormData({
      personnel_id: '',
      approbation: true
    });
    setSelectedPersonnel(null);
    setPersonnelSearch('');
    setShowPersonnelDropdown(false);
    setShowModal(true);
  };

  const handleDelete = (caisse) => {
    setSelectedCaisse(caisse);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await http.delete(`/api/caisse-sociale/${selectedCaisse.id}`);
      alert('Employé supprimé de la caisse sociale avec succès');
      setShowDeleteModal(false);
      loadData();
    } catch (error) {
      console.error('Erreur suppression:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const handlePersonnelSearchChange = (e) => {
    setPersonnelSearch(e.target.value);
    setShowPersonnelDropdown(e.target.value.length > 0);
  };

  const handlePersonnelSelect = (personnel) => {
    setSelectedPersonnel(personnel);
    setFormData({ ...formData, personnel_id: personnel.idpersonnel });
    setPersonnelSearch('');
    setShowPersonnelDropdown(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await http.post('/api/caisse-sociale', formData);
      alert('Employé ajouté à la caisse sociale avec succès');
      setShowModal(false);
      loadData();
    } catch (error) {
      console.error('Erreur soumission:', error);
      alert('Erreur lors de la soumission');
    }
  };

  const handleExportExcel = () => {
    const data = caisses.map(c => ({
      'Nom': c.nom,
      'Prénom': c.prenom,
      'Matricule': c.matricule,
      'Date de souscription': c.date_souscription,
      'Approuvé': c.approbation ? 'Oui' : 'Non'
    }));

    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Caisse sociale');
    XLSX.writeFile(wb, 'caisse_sociale.xlsx');
  };

  const filteredCaisses = caisses.filter(c =>
    c.nom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.prenom?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.matricule?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const tauxApprobation = stats.total > 0 ? ((stats.approuves / stats.total) * 100).toFixed(1) : 0;

  if (loading) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-lg text-gray-500">Chargement...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <BlueHeader
        title="Caisse Sociale"
        subtitle="Gérer les souscriptions à la caisse sociale"
        actions={[
          {
            label: 'Ajouter',
            icon: Plus,
            onClick: handleCreate,
            primary: true
          },
          {
            label: 'Actualiser',
            icon: RefreshCw,
            onClick: loadData,
            primary: false
          },
          {
            label: 'Exporter',
            icon: null,
            onClick: handleExportExcel,
            primary: false
          }
        ]}
      />

      <div className="px-6 py-8">
        {/* Indicateurs clés */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Total souscrits</p>
                <p className="text-3xl font-bold text-gray-900">{stats.total}</p>
              </div>
              <div className="bg-blue-100 p-3 rounded-full">
                <Users className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Approuvés</p>
                <p className="text-3xl font-bold text-gray-900">{stats.approuves}</p>
                <p className="text-xs text-gray-500 mt-1">{tauxApprobation}% du total</p>
              </div>
              <div className="bg-green-100 p-3 rounded-full">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-red-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Non approuvés</p>
                <p className="text-3xl font-bold text-gray-900">{stats.non_approuves}</p>
                <p className="text-xs text-gray-500 mt-1">{((stats.non_approuves / stats.total) * 100).toFixed(1)}% du total</p>
              </div>
              <div className="bg-red-100 p-3 rounded-full">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Aujourd'hui</p>
                <p className="text-3xl font-bold text-gray-900">{stats.today}</p>
                <p className="text-xs text-gray-500 mt-1">Nouvelles souscriptions</p>
              </div>
              <div className="bg-purple-100 p-3 rounded-full">
                <Calendar className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-orange-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-500">Montant paie en cours</p>
                <p className="text-3xl font-bold text-gray-900">{montantPaie.montant.toLocaleString()}</p>
                <p className="text-xs text-gray-500 mt-1">{currentFiliale?.devise || 'FCFA'}</p>
              </div>
              <div className="bg-orange-100 p-3 rounded-full">
                <Wallet className="h-6 w-6 text-orange-600" />
              </div>
            </div>
          </div>
        </div>

        {/* Graphique d'évolution */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-8">
          <h3 className="text-lg font-semibold text-gray-800 mb-4">Évolution des souscriptions (12 derniers mois)</h3>
          <div className="h-64">
            {evolution.length > 0 ? (
              <div className="flex items-end justify-between h-full gap-2">
                {evolution.slice().reverse().map((e, index) => {
                  const max = Math.max(...evolution.map(ev => ev.nombre));
                  const height = max > 0 ? (e.nombre / max) * 100 : 0;
                  return (
                    <div key={index} className="flex-1 flex flex-col items-center">
                      <div 
                        className="w-full bg-blue-500 rounded-t transition-all hover:bg-blue-600"
                        style={{ height: `${height}%`, minHeight: '4px' }}
                        title={`${e.nombre} souscriptions`}
                      />
                      <div className="text-xs text-gray-500 mt-2 text-center">
                        {e.mois.split('-')[1]}/{e.mois.split('-')[0].slice(2)}
                      </div>
                      <div className="text-xs font-medium text-gray-700">{e.nombre}</div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="flex items-center justify-center h-full text-gray-500">
                Aucune donnée d'évolution disponible
              </div>
            )}
          </div>
        </div>

        {/* Recherche */}
        <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Rechercher par nom, prénom ou matricule..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
            />
          </div>
        </div>

        {/* Tableau */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-800">Liste des souscriptions</h3>
            <p className="text-sm text-gray-500 mt-1">{filteredCaisses.length} souscription(s)</p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Nom & Prénom</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Matricule</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date de souscription</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Approuvé</th>
                  <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredCaisses.length === 0 ? (
                  <tr>
                    <td colSpan="5" className="px-6 py-12 text-center text-gray-500">
                      Aucune souscription trouvée
                    </td>
                  </tr>
                ) : (
                  filteredCaisses.map((c) => (
                    <tr key={c.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 text-sm font-medium text-gray-900">
                        {c.nom} {c.prenom}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600">{c.matricule}</td>
                      <td className="px-6 py-4 text-sm text-gray-600">{c.date_souscription}</td>
                      <td className="px-6 py-4 text-sm">
                        {c.approbation ? (
                          <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 rounded-full text-xs">
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Oui
                          </span>
                        ) : (
                          <span className="inline-flex items-center px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs">
                            <XCircle className="h-3 w-3 mr-1" />
                            Non
                          </span>
                        )}
                      </td>
                      <td className="px-6 py-4 text-center">
                        <button
                          onClick={() => handleDelete(c)}
                          className="text-red-600 hover:text-red-800"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Modal d'ajout */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white shadow-2xl border border-blue-100">
            <div className="mb-4 flex items-center justify-between border-b border-blue-100 pb-4 px-6">
              <h3 className="text-lg font-bold text-blue-900">Ajouter à la caisse sociale</h3>
              <button onClick={() => setShowModal(false)} className="text-gray-500 hover:text-gray-900">✕</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="p-6 space-y-4">
                <div>
                  <label className="mb-1.5 block text-sm font-medium text-gray-700">Employé <span className="text-red-500">*</span></label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Rechercher par nom, prénom ou matricule..."
                      value={personnelSearch}
                      onChange={handlePersonnelSearchChange}
                      onFocus={() => setShowPersonnelDropdown(personnelSearch.length > 0)}
                      className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
                    />
                    {showPersonnelDropdown && personnelSearch.length > 0 && (
                      <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-60 overflow-y-auto">
                        {personnel
                          .filter(p =>
                            p.nom?.toLowerCase().includes(personnelSearch.toLowerCase()) ||
                            p.prenom?.toLowerCase().includes(personnelSearch.toLowerCase()) ||
                            p.matricule?.toLowerCase().includes(personnelSearch.toLowerCase())
                          )
                          .slice(0, 10)
                          .map((p) => (
                            <div
                              key={p.idpersonnel}
                              onClick={() => handlePersonnelSelect(p)}
                              className="px-4 py-2 hover:bg-gray-50 cursor-pointer border-b border-gray-100 last:border-0"
                            >
                              <div className="text-sm font-medium text-gray-900">{p.nom} {p.prenom}</div>
                              <div className="text-xs text-gray-500">{p.matricule}</div>
                            </div>
                          ))}
                        {personnel.filter(p =>
                          p.nom?.toLowerCase().includes(personnelSearch.toLowerCase()) ||
                          p.prenom?.toLowerCase().includes(personnelSearch.toLowerCase()) ||
                          p.matricule?.toLowerCase().includes(personnelSearch.toLowerCase())
                        ).length === 0 && (
                          <div className="px-4 py-2 text-sm text-gray-500">Aucun employé trouvé</div>
                        )}
                      </div>
                    )}
                  </div>
                  {selectedPersonnel && (
                    <div className="mt-2 inline-flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-900 rounded-full text-sm">
                      {selectedPersonnel.nom} {selectedPersonnel.prenom}
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedPersonnel(null);
                          setFormData({ ...formData, personnel_id: '' });
                        }}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        ✕
                      </button>
                    </div>
                  )}
                </div>
                <div>
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={formData.approbation}
                      onChange={(e) => setFormData({ ...formData, approbation: e.target.checked })}
                      className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm font-medium text-gray-700">Approuvé pour la caisse sociale</span>
                  </label>
                </div>
              </div>
              <div className="flex justify-end gap-3 border-t border-blue-100 p-6">
                <button
                  type="button"
                  onClick={() => setShowModal(false)}
                  className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={!formData.personnel_id}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Enregistrer
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal de suppression */}
      {showDeleteModal && selectedCaisse && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-2xl border border-red-100">
            <div className="mb-4">
              <h3 className="text-lg font-bold text-red-900">Supprimer de la caisse sociale</h3>
              <p className="text-sm text-gray-600 mt-2">
                Êtes-vous sûr de vouloir supprimer {selectedCaisse.nom} {selectedCaisse.prenom} de la caisse sociale ?
              </p>
              <p className="text-sm text-gray-500 mt-2">
                Cette action est irréversible.
              </p>
            </div>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded-md hover:bg-gray-300"
              >
                Annuler
              </button>
              <button
                onClick={handleDeleteConfirm}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Supprimer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CaisseSociale;
